// Simple IP geolocation service
// Uses a free API to get approximate coordinates

const geoCache = new Map()

const ipToLocation = async (ip) => {
  // Check cache first
  if (geoCache.has(ip)) {
    return geoCache.get(ip)
  }

  // Handle localhost IPv6 address
  if (ip === '::ffff:127.0.0.1') {
    const parisLocation = {
      country: 'France',
      city: 'Paris',
      lat: 48.8566,
      lon: 2.3522
    }
    geoCache.set(ip, parisLocation)
    return parisLocation
  }

  // Skip private/local IPs
  if (!ip || ip === 'unknown' || ip.startsWith('127.') || ip.startsWith('192.168.') || ip.startsWith('10.')) {
    return null
  }

  try {
    // Use a free API (rate limits apply)
    const response = await fetch(`${import.meta.env.VITE_API_URL}/ipinfo/json/${ip}?fields=status,country,city,lat,lon`)
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    
    const data = await response.json()
    
    if (data.status === 'success') {
      const location = {
        country: data.country,
        city: data.city,
        lat: data.lat,
        lon: data.lon
      }
      
      geoCache.set(ip, location)
      return location
    } else {
      geoCache.set(ip, null)
      return null
    }
  } catch (error) {
    console.error(`Geolocation error for IP ${ip}:`, error)
    // Default coordinates
    const defaultLocation = { lat: 0, lng: 0, country: 'Unknown', city: 'Unknown' }
    geoCache.set(ip, defaultLocation)
    return defaultLocation
  }
}

const getLocationsFromIPs = async (ips) => {
  const locations = {}
  
  for (const ip of ips) {
    try {
      const location = await ipToLocation(ip)
      if (location) {
        locations[ip] = location
      }
      
      // Small delay to avoid overloading the API
      await new Promise(resolve => setTimeout(resolve, 100))
    } catch (error) {
      console.error(`Error for IP ${ip}:`, error)
    }
  }
  
  return locations
}

// Simple equirectangular projection for world map SVG
function projectToMap(lat, lng, width, height) {
  // Longitude: -180 (left) to 180 (right)
  // Latitude:  90 (top) to -90 (bottom)
  const x = ((lng + 180) / 360) * width
  const y = ((90 - lat) / 180) * height
  return { x, y }
}

export const geoService = {
  ipToLocation,
  getLocationsFromIPs,
  projectToMap
} 